<template>
  <component
    :is="tag"
    class="app-btn"
    :class="btnClasses"
    :disabled="disabled || loading"
    :type="tag === 'button' ? (nativeType || 'button') : null"
    :href="href"
    :style="btnStyle"
    v-bind="$attrs"
    @click="onClick"
  >
    <span v-if="loading" class="app-btn__spinner" />
    <span v-else-if="icon" class="material-icons app-btn__icon">{{ icon }}</span>
    <span v-if="$slots.default || label" class="app-btn__text">
      <slot>{{ label }}</slot>
    </span>
  </component>
</template>

<script>
import { variantStyle } from './variants.js'

export default {
  name: 'AppButton',
  inheritAttrs: false,
  props: {
    variant: { type: String, default: 'secondary' },
    outline: { type: Boolean, default: false },
    size: { type: String, default: null },
    block: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    pill: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    label: { type: String, default: '' },
    tag: { type: String, default: 'button' },
    href: { type: String, default: null },
    type: { type: String, default: 'button' },
    to: { type: [String, Object], default: null }
  },
  emits: ['click'],
  computed: {
    nativeType() {
      if (this.href || this.to) return null
      return this.type
    },
    btnClasses() {
      return [
        'app-btn--' + this.size,
        { 'app-btn--block': this.block },
        { 'app-btn--pill': this.pill },
        { 'app-btn--loading': this.loading },
        { 'app-btn--disabled': this.disabled }
      ]
    },
    btnStyle() {
      return variantStyle(this.variant, this.outline)
    }
  },
  methods: {
    onClick(event) {
      if (this.to && this.$router) {
        this.$router.push(this.to)
      }
      this.$emit('click', event)
    }
  }
}
</script>

<style scoped>
.app-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  text-decoration: none;
  transition: all 0.15s ease;
}
.app-btn:hover {
  background-color: var(--hover-bg);
  color: var(--hover-color);
}
.app-btn:active { filter: brightness(0.92); }
.app-btn:focus { outline: 0; box-shadow: 0 0 0 0.2rem var(--hover-bg); }

.app-btn--sm  { padding: 0.25rem 0.5rem; font-size: 0.875rem; border-radius: 0.2rem; }
.app-btn--lg  { padding: 0.5rem 1rem; font-size: 1.25rem; border-radius: 0.3rem; }
.app-btn--block { display: flex; width: 100%; }
.app-btn--pill  { border-radius: 50rem; }

.app-btn--loading,
.app-btn--disabled { opacity: 0.65; pointer-events: none; }

.app-btn__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: app-spin 0.6s linear infinite;
}
.app-btn__icon { font-size: 18px; }
.app-btn__text  { /* allow text alignment */ }

@keyframes app-spin {
  to { transform: rotate(360deg); }
}
</style>
